import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { spacing } from "../../styles/theme";
import PartPickerLeftMenu from "./PartPickerLeftMenu";
import { SpecsInputField, SpecsSelectField } from "./SpecsEditorFields/index";
import type { PartModel } from "./specsModel/PartModel";
import { useSpecsEditorContextValue } from "./useSpecsEditor";

export default function SpecsPartsPage() {
  const {
    currentVersion,
    nav,
    addPart,
    modals,
    moveElementToPart,
    removePart,
    renamePart,
    reorderPart,
    selectParts,
    updatePart,
  } = useSpecsEditorContextValue();
  const parts = currentVersion?.parts ?? [];
  const requestedPartName = nav.kind === "parts" ? nav.part : undefined;
  const selectedPart =
    parts.find((part) => part.name === requestedPartName) ?? parts[0] ?? null;
  const selectedPartIndex = selectedPart
    ? parts.findIndex((part) => part.name === selectedPart.name)
    : -1;
  const openAddPartPrompt = () =>
    modals.openPromptDialog("Add Part", "Part name", "", addPart);

  React.useEffect(() => {
    if (!selectedPart) return;
    if (requestedPartName === selectedPart.name) return;
    selectParts(selectedPart.name);
  }, [requestedPartName, selectParts, selectedPart]);

  return (
    <Paper variant="outlined" sx={{ p: spacing.groupingDesktop }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h2">Parts</Typography>
      </Stack>

      {!currentVersion ? (
        <Alert severity="info">
          Select a product and version to edit parts.
        </Alert>
      ) : !selectedPart ? (
        <Stack spacing={2}>
          <Alert severity="info">
            This version does not have any parts yet.
          </Alert>
          <Box>
            <Button variant="contained" onClick={openAddPartPrompt}>
              Add part
            </Button>
          </Box>
        </Stack>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "240px minmax(0, 1fr)" },
            gap: 3,
          }}
        >
          <PartPickerLeftMenu
            parts={parts}
            selectedPartName={selectedPart.name}
            footerActionLabel="Add part"
            onFooterAction={openAddPartPrompt}
            onSelectPart={selectParts}
          />

          <SelectedPartEditor
            part={selectedPart}
            partIndex={selectedPartIndex}
            parts={parts}
            onDelete={(partName) => {
              if (
                window.confirm(
                  `Supprimer la part "${partName}" et tous ses elements?`
                )
              ) {
                removePart(partName);
              }
            }}
            onMoveElement={moveElementToPart}
            onRename={(partName) =>
              modals.openPromptDialog(
                "Rename Part",
                "Part name",
                partName,
                (nextPartName) => renamePart(partName, nextPartName)
              )
            }
            onReorder={reorderPart}
            onUpdate={updatePart}
          />
        </Box>
      )}
    </Paper>
  );
}

type SelectedPartEditorProps = {
  part: PartModel;
  partIndex: number;
  parts: PartModel[];
  onDelete: (partName: string) => void;
  onMoveElement: (
    fromPartName: string,
    elementName: string,
    toPartName: string
  ) => void;
  onRename: (partName: string) => void;
  onReorder: (partName: string, targetIndex: number) => void;
  onUpdate: (
    partName: string,
    patch: Partial<{
      mainElement: string;
      required: boolean;
      inheritFrom: string | null;
    }>
  ) => void;
};

function SelectedPartEditor({
  part,
  partIndex,
  parts,
  onDelete,
  onMoveElement,
  onRename,
  onReorder,
  onUpdate,
}: SelectedPartEditorProps) {
  const debugPath = part._debugPath;
  const extraEntries = Object.keys(part.extra);
  const partOptions = parts.map((candidate) => ({
    value: candidate.name,
    label: candidate.name,
  }));

  return (
    <Stack spacing={3}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="h3">{part.name}</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {part.elements.length} element
            {part.elements.length === 1 ? "" : "s"}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            disabled={partIndex <= 0}
            onClick={() => onReorder(part.name, partIndex - 1)}
          >
            Move up
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={partIndex >= parts.length - 1}
            onClick={() => onReorder(part.name, partIndex + 1)}
          >
            Move down
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onRename(part.name)}
          >
            Renommer
          </Button>
          <Button
            size="small"
            variant="text"
            color="error"
            disabled={parts.length <= 1}
            onClick={() => onDelete(part.name)}
          >
            Supprimer
          </Button>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <SpecsInputField
          label="Name"
          debugPath={debugPath ? `${debugPath}.name` : undefined}
          value={part.name}
          disabled
        />

        <SpecsSelectField
          label="Main element"
          debugPath={debugPath ? `${debugPath}.mainElement` : undefined}
          value={part.mainElement}
          options={[
            { value: "", label: "Aucun" },
            ...part.elements.map((element) => ({
              value: element.name,
              label: element.name,
            })),
          ]}
          onChange={(mainElement) => onUpdate(part.name, { mainElement })}
        />

        <FormControlLabel
          sx={{ alignSelf: "end", minHeight: 58 }}
          control={
            <Checkbox
              checked={part.required}
              onChange={(_, required) => onUpdate(part.name, { required })}
            />
          }
          label="Required"
        />

        <SpecsSelectField
          label="Inherit from"
          debugPath={debugPath ? `${debugPath}.inheritFrom` : undefined}
          value={part.inheritFrom ?? ""}
          options={[
            { value: "", label: "Aucun" },
            ...parts
              .filter((candidate) => candidate.name !== part.name)
              .map((candidate) => ({
                value: candidate.name,
                label: candidate.name,
              })),
          ]}
          onChange={(inheritFrom) =>
            onUpdate(part.name, {
              inheritFrom: inheritFrom || null,
            })
          }
        />

        {extraEntries.length > 0 ? (
          <SpecsInputField
            label="Extra JSON"
            debugPath={debugPath ? `${debugPath}.extra` : undefined}
            value={JSON.stringify(part.extra, null, 2)}
            multiline
            minRows={4}
            disabled
            sx={{ gridColumn: { md: "1 / -1" } }}
          />
        ) : null}
      </Box>

      <Stack spacing={1.5}>
        <Typography variant="h4">Éléments</Typography>

        {part.elements.length === 0 ? (
          <Alert severity="info">No elements assigned to this part.</Alert>
        ) : (
          <Stack spacing={1}>
            {part.elements.map((element) => (
              <Box
                key={element.name}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 220px" },
                  gap: 2,
                  alignItems: "center",
                  py: 1,
                  borderTop: "1px solid rgba(0,0,0,0.08)",
                }}
              >
                <Typography variant="body1">{element.name}</Typography>
                <SpecsSelectField
                  label={`Move ${element.name} to`}
                  value={part.name}
                  options={partOptions}
                  onChange={(targetPartName) =>
                    onMoveElement(part.name, element.name, targetPartName)
                  }
                />
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
